import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Tabs } from "@opendate-app/setlist";

import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useBroadcastEditContext } from "./BroadcastEditContainer";
import { TwilioTemplatableProvider, useTwilioTemplatableContext } from "../../Marketing/TwilioTemplates/TwilioTemplatableContext";
import TwilioTemplateErrorsContainer from "../../Marketing/TwilioTemplates/TwilioTemplateErrorsContainer";
import TemplateEditor from "../../Marketing/TwilioTemplates/TemplateEditor";
import SendTestOverlay from "../../Marketing/TwilioTemplates/SendTestOverlay";
import ContainerlessTableViewApp from "../../TableView/startup/ContainerlessTableViewApp";
import { DeliveryDateField, RecipientsSummary } from "./DeliverySummary";

const styles = {
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
  },
  tabRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  tabs: {
    flex: "1 1 320px",
    minWidth: 0,
  },
  tabRowRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  panel: {
    boxShadow: "var(--sl-shadow-sm)",
    backgroundColor: "var(--sl-bg-primary)",
  },
  sent: {
    display: "grid",
    width: "100%",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 24,
  },
};

const TwilioEditorTab = () => {
  const {
    csrfToken,
    team,
    broadcast,
    setBroadcast,
    scheduledSend,
    twilioMessagingServices,
    setTab,
    sampleTwilioData,
    twilioMergeTags,
    previouslySent,
  } = useBroadcastEditContext();

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [processedTo, setProcessedTo] = useState("...");
  const [template, setTemplate] = useState(broadcast.twilio_template.template || "");
  const [processedTemplate, setProcessedTemplate] = useState("...");
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [editorTab, setEditorTab] = useState("Content");

  const to = "+1{{fan.phone_number}}";

  const editorTabs = [
    { value: "Content", label: "Content" },
    ...(scheduledSend ? [{ value: "Details", label: "Details" }] : []),
    ...(previouslySent ? [{ value: "Sent", label: "Sent" }] : []),
  ];

  useEffect(() => {
    setSaveDisabled(template === broadcast.twilio_template.template);
  }, [template, broadcast.twilio_template.template]);

  const saveCurrentTemplate = () => {
    updateTwilioTemplate({
      template: template,
    });
  };

  const defaultMessagingServiceId = () => {
    if (broadcast.twilio_template.twilio_messaging_service_id) {
      return broadcast.twilio_template.twilio_messaging_service_id;
    }
    if (twilioMessagingServices?.length === 1) {
      return twilioMessagingServices[0].id;
    }
    return null;
  };

  const updateTwilioTemplate = (payload) => {
    if (isLoading) {
      return;
    }

    const messagingServiceId = payload.twilio_messaging_service_id || defaultMessagingServiceId();

    setIsLoading(true);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`/teams/${team.id}/broadcasts/${broadcast.id}`, {
      twilio_template_attributes: {
        ...payload,
        ...(messagingServiceId && { twilio_messaging_service_id: messagingServiceId }),
        id: broadcast.twilio_template.id,
      }
    })
      .then(({ data }) => {
        setBroadcast(data);
        toastSuccess("SMS template updated successfully.");
      })
      .catch((_error) => {
        toastError("Unable to update SMS template.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleBack = () => {
    if (scheduledSend || previouslySent) {
      window.location = `/teams/${team.id}/broadcasts`;
    } else {
      setTab("twilio-content");
    }
  }

  return (
    <TwilioTemplatableProvider
      templatable={broadcast}
      templatableContext={useBroadcastEditContext}
      template={template}
      setTemplate={setTemplate}
      processedTemplate={processedTemplate}
      setProcessedTemplate={setProcessedTemplate}
      sampleData={sampleTwilioData}
      errors={errors}
      setErrors={setErrors}
      twilioMessagingServices={twilioMessagingServices}
      updateTwilioTemplate={updateTwilioTemplate}
      to={to}
      processedTo={processedTo}
      setProcessedTo={setProcessedTo}
      testModalOpen={testModalOpen}
      setTestModalOpen={setTestModalOpen}
    >
      <SendTestOverlay templatableContext={useTwilioTemplatableContext} />
      <div style={styles.actions}>
        <Button variant="link-color" onClick={handleBack}>
          Back
        </Button>
        <Button variant="primary" disabled={saveDisabled || isLoading} onClick={saveCurrentTemplate}>
          Save
        </Button>
      </div>
      <div style={styles.tabRow}>
        <div style={styles.tabs}>
          <Tabs items={editorTabs} value={editorTab} onChange={setEditorTab} />
        </div>
        <div style={styles.tabRowRight}>
          <TwilioTemplateErrorsContainer templatableContext={useTwilioTemplatableContext} />
          {broadcast.twilio_template.twilio_messaging_service_id && (
            <Button variant="secondary-color" onClick={() => setTestModalOpen(true)}>
              Send Test
            </Button>
          )}
        </div>
      </div>
      <div style={styles.panel}>
        {editorTab === "Content" ? (
          <TemplateEditor
            templatableContext={useTwilioTemplatableContext}
            mergeTags={twilioMergeTags}
          />
        ) : editorTab === "Details" ? (
          <div style={styles.details}>
            <DeliveryDateField />
            <RecipientsSummary bestPracticeText="Sending SMS to everyone in your database can damage your sending reputation and deliverability." />
          </div>
        ) : (
          <div style={styles.sent}>
            <ContainerlessTableViewApp
              dataSource={`/teams/${team.id}/twilio_messages?trackable_type=Broadcast&trackable_id=${broadcast.id}`}
              order={[[0, "desc"]]}
              columns={[
                { label: "Date Sent", data: "created_at" },
                { label: "Body", data: "body", orderable: false },
                { label: "Recipient", data: "recipient", orderable: false },
                { label: "Status", data: "status", orderable: false },
              ]}
            />
          </div>
        )}
      </div>
    </TwilioTemplatableProvider>
  );
};

export default TwilioEditorTab;
